import React, { useEffect, useState } from "react";
import TaxonFilter from "./TaxonFilter";
import { Range } from "react-range";
import ReactGA from "react-ga4";
import { usePostHogTracking } from "./hooks/usePostHogTracking";
import { CARE_GUIDE_DIET_TRAIT_LABELS } from "./utils/careGuideQuickFacts";
import { sizeNameMap } from "./utils/sizeNameUtils";
import SearchableSelect from "./components/SearchableSelect";
import SearchableCheckboxList from "./components/SearchableCheckboxList";
import PersonalListsFilter from "./components/PersonalListsFilter";
import ResponsiveFilterSection from "./components/ResponsiveFilterSection";

const Filters = ({
  searchTerm,
  setSearchTerm,
  categoryFilter,
  setCategoryFilter,
  sizeFilter,
  onSizeFilterChange,
  sizeRange,
  allowedStores,
  setAllowedStores,
  storesData,
  availableStores = [],
  filteredCategories,
  unfilteredCategories,
  filteredSizes,
  unfilteredSizes,
  sortSizes,
  filteredClasses,
  unfilteredClasses,
  classFilter,
  setClassFilter,
  filteredOrders,
  unfilteredOrders,
  orderFilter,
  setOrderFilter,
  filteredFamilies,
  unfilteredFamilies,
  familyFilter,
  setFamilyFilter,
  filteredGenera,
  unfilteredGenera,
  genusFilter,
  setGenusFilter,
  filteredScientificNames,
  unfilteredScientificNames,
  scientificFilter,
  setScientificFilter,
  speciesMap,
  nonNumericSizeFilter,
  setNonNumericSizeFilter,
  sortOrder,
  setSortOrder,
  sortOptions,
  includeNumericSizes,
  setIncludeNumericSizes,
  includeNonNumericSizes,
  setIncludeNonNumericSizes,
  availableNonNumericSizes,
  freshwaterFilter,
  setFreshwaterFilter,
  saltwaterFilter,
  setSaltwaterFilter,
  guideAnimalFilter,
  setGuideAnimalFilter,
  guideInvertFilter,
  setGuideInvertFilter,
  guidePlantFilter,
  setGuidePlantFilter,
  brackishFilter,
  setBrackishFilter,
  favoritesOnly,
  setFavoritesOnly,
  notificationsOnly,
  setNotificationsOnly,
  reefWizardOnly,
  setReefWizardOnly,
  reefWizardTankFilter,
  setReefWizardTankFilter,
  reefWizardTanks = [],
  hideSort = false,
  hideSize = false,
  hideStoreFilter = false,
  hidePersonalLists = false,
  careGuidesData = [],
  minTankSizeMax,
  setMinTankSizeMax,
  maxSizeMax,
  setMaxSizeMax,
  temperatureRange,
  setTemperatureRange,
  phRange,
  setPhRange,
  careLevels,
  setCareLevels,
  reefSafeValues,
  setReefSafeValues,
  plantSafeValues,
  setPlantSafeValues,
  temperamentValues,
  setTemperamentValues,
  dietTraits,
  setDietTraits,
  lifespanRange,
  setLifespanRange,
  availableCareLevels = [],
  availableReefSafe = [],
  availablePlantSafe = [],
  availableTemperaments = [],
  availableDietTraits = [],
  temperatureBounds = [60, 90],
  maxTankSizeBound = 500,
  maxSizeBound = 24,
  phBounds = [6, 9],
  lifespanBounds = null,
  hideCareRequirements = false,
  hasCareGuide = "all",
  setHasCareGuide,
  hideTaxonomy = false,
  hideSpecies = false,
  hideCategory = false,
  hideSearch = false,
  hideWater = false,
}) => {
  const { trackFilterUsage } = usePostHogTracking();
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const numericSizeMin = sizeRange?.[0] ?? 0;
  const numericSizeMax = sizeRange?.[1] ?? numericSizeMin;
  const clampNumericSize = (value) =>
    Math.min(numericSizeMax, Math.max(numericSizeMin, value));
  const clampedNumericSizes = [
    clampNumericSize(sizeFilter?.[0] ?? numericSizeMin),
    clampNumericSize(sizeFilter?.[1] ?? numericSizeMax),
  ].sort((left, right) => left - right);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchTerm(localSearchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [localSearchTerm, setSearchTerm]);

  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  const handleSortOrderChange = (event) => {
    const newSortOrder = event.target.value;
    ReactGA.event({
      category: "Filters",
      action: "Set Sort Order",
      label: newSortOrder,
    });
    trackFilterUsage("sort_order", newSortOrder);
    setSortOrder(newSortOrder);
  };

  const handleNonNumericSizeChange = (size) => {
    setNonNumericSizeFilter((prev) =>
      prev.includes(size) ? prev.filter((value) => value !== size) : [...prev, size]
    );
  };

  return (
    <div className="mt-1 max-h-[80vh] filter-sidebar-content overflow-y-auto bg-indigo-50 p-3 rounded-lg shadow">
      {(!hideSearch || !hideCategory || !hideSort) && (
        <ResponsiveFilterSection
          title="Search & sort"
          defaultOpen
          active={Boolean(searchTerm || categoryFilter)}
        >
      {!hideSearch && (
        <div className="mb-3 bg-gray-50 p-3 rounded-lg shadow">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search
          </label>
          <input
            type="text"
            placeholder="Search by name..."
            className="w-full p-2 border border-gray-300 rounded"
            value={localSearchTerm}
            onChange={(event) => setLocalSearchTerm(event.target.value)}
            data-posthog="filter-search"
            data-posthog-search-term={searchTerm}
          />
        </div>
      )}

      {!hideCategory && (
        <div className="mb-3 bg-gray-50 p-3 rounded-lg shadow">
          <SearchableSelect
            label="Category"
            value={categoryFilter}
            onChange={setCategoryFilter}
            options={filteredCategories}
            unfilteredOptions={unfilteredCategories}
            placeholder="Select category..."
            allOptionLabel="All Categories"
            posthogId="filter-category"
            posthogAttribute="data-posthog-category"
          />
        </div>
      )}

      {!hideSort && (
        <div className="mb-3 bg-gray-50 p-3 rounded-lg shadow">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sort Order
          </label>
          <select
            className="w-full p-2 border border-gray-300 rounded"
            value={sortOrder}
            onChange={handleSortOrderChange}
            data-posthog="filter-sort-order"
            data-posthog-sort-order={sortOrder}
          >
            {Object.keys(sortOptions).map((key) => (
              <option key={key} value={key}>
                {key
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (value) => value.toUpperCase())}
              </option>
            ))}
          </select>
        </div>
      )}
        </ResponsiveFilterSection>
      )}

      {(!hideSize ||
        (setGuideAnimalFilter && setGuideInvertFilter && setGuidePlantFilter) ||
        !hideWater) && (
        <ResponsiveFilterSection
          title="Size & habitat"
        >
      {!hideSize && (
        <div className="flex flex-row flex-wrap justify-between items-center gap-2 mb-3 bg-gray-50 p-3 rounded-lg shadow">
          <label className="block text-base font-medium text-gray-700 mb-1 w-full">
            Size
          </label>

          <label className="block text-sm font-medium text-gray-700 mb-1 w-full">
            Numeric Size
          </label>
          <div className="bg-gray-100 p-2 rounded-lg shadow basis-0 grow text-nowrap grow mb-3 w-full">
            <div className="flex flex-row gap-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="form-checkbox"
                  checked={includeNumericSizes}
                  onChange={(event) => setIncludeNumericSizes(event.target.checked)}
                  data-posthog="filter-size-numeric-all"
                />
                Include Numeric Size
              </label>
            </div>
            <div className={"mt-3 " + (!includeNumericSizes && " hidden")}>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Size Range
              </label>
              <Range
                step={1}
                min={numericSizeMin}
                max={numericSizeMax}
                values={clampedNumericSizes}
                onChange={onSizeFilterChange}
                renderTrack={({ props, children }) => (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      height: "6px",
                      background: `linear-gradient(to right, #ccc ${
                        ((clampedNumericSizes[0] - numericSizeMin) /
                          (numericSizeMax - numericSizeMin)) *
                        100
                      }%, #007bff ${
                        ((clampedNumericSizes[0] - numericSizeMin) /
                          (numericSizeMax - numericSizeMin)) *
                        100
                      }%, #007bff ${
                        ((clampedNumericSizes[1] - numericSizeMin) /
                          (numericSizeMax - numericSizeMin)) *
                        100
                      }%, #ccc ${
                        ((clampedNumericSizes[1] - numericSizeMin) /
                          (numericSizeMax - numericSizeMin)) *
                        100
                      }%)`,
                      borderRadius: "3px",
                    }}
                  >
                    {children}
                  </div>
                )}
                renderThumb={({ props }) => (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      height: "16px",
                      width: "16px",
                      backgroundColor: "#007bff",
                      borderRadius: "50%",
                    }}
                  />
                )}
              />
              <div className="flex justify-between text-sm mt-2">
                <span>{clampedNumericSizes[0]}"</span>
                <span>{clampedNumericSizes[1]}"</span>
              </div>
            </div>
          </div>

          <label className="block text-sm font-medium text-gray-700 mb-1 w-full">
            Non-Numeric Sizes
          </label>
          <div className="bg-gray-100 p-2 rounded-lg shadow basis-0 grow text-nowrap">
            <div className="flex flex-row gap-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="form-checkbox"
                  checked={includeNonNumericSizes}
                  onChange={(event) => setIncludeNonNumericSizes(event.target.checked)}
                  data-posthog="filter-size-nonnumeric-all"
                />
                Include Non-Numeric Sizes
              </label>
            </div>
            <div
              className={
                "mt-4 flex flex-col flex-nowrap gap-2 max-h-[20vh] overflow-y-auto " +
                (!includeNonNumericSizes && " hidden")
              }
            >
              {Array.from(new Set(Object.values(availableNonNumericSizes)))
                .sort((a, b) => {
                  const indexA = Object.values(sizeNameMap).indexOf(a);
                  const indexB = Object.values(sizeNameMap).indexOf(b);
                  return indexA - indexB;
                })
                .map((size) => (
                  <label
                    key={size}
                    className="bg-white p-2 rounded-lg shadow flex-1 text-nowrap"
                  >
                    <input
                      type="checkbox"
                      className="form-checkbox"
                      checked={nonNumericSizeFilter.includes(size)}
                      onChange={() => handleNonNumericSizeChange(size)}
                      data-posthog="filter-size-nonnumeric"
                      data-posthog-size-name={size}
                    />
                    <span className="ml-2">{size}</span>
                  </label>
                ))}
            </div>
          </div>
        </div>
      )}

      {setGuideAnimalFilter && setGuideInvertFilter && setGuidePlantFilter && (
        <div className="mb-3 bg-gray-50 p-3 rounded-lg shadow">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Guide Type
          </label>
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 bg-white p-2 rounded-lg shadow flex-1 text-nowrap">
              <input
                type="checkbox"
                className="form-checkbox"
                checked={guideAnimalFilter}
                onChange={(event) => {
                  setGuideAnimalFilter(event.target.checked);
                  trackFilterUsage("care_guide_type_animals", event.target.checked ? "included" : "excluded");
                }}
                data-posthog="filter-guide-type-animals"
              />
              Animals
            </label>
            <label className="flex items-center gap-2 bg-white p-2 rounded-lg shadow flex-1 text-nowrap">
              <input
                type="checkbox"
                className="form-checkbox"
                checked={guideInvertFilter}
                onChange={(event) => {
                  setGuideInvertFilter(event.target.checked);
                  trackFilterUsage("care_guide_type_inverts", event.target.checked ? "included" : "excluded");
                }}
                data-posthog="filter-guide-type-inverts"
              />
              Inverts
            </label>
            <label className="flex items-center gap-2 bg-white p-2 rounded-lg shadow flex-1 text-nowrap">
              <input
                type="checkbox"
                className="form-checkbox"
                checked={guidePlantFilter}
                onChange={(event) => {
                  setGuidePlantFilter(event.target.checked);
                  trackFilterUsage("care_guide_type_plants", event.target.checked ? "included" : "excluded");
                }}
                data-posthog="filter-guide-type-plants"
              />
              Plants
            </label>
          </div>
        </div>
      )}

      {!hideWater && (
        <div className="mb-3 bg-gray-50 p-3 rounded-lg shadow">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Water
          </label>
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 bg-white p-2 rounded-lg shadow flex-1 text-nowrap">
              <input
                type="checkbox"
                className="form-checkbox"
                checked={freshwaterFilter}
                onChange={(event) => setFreshwaterFilter(event.target.checked)}
                data-posthog="filter-water-freshwater"
              />
              Freshwater
            </label>
            <label className="flex items-center gap-2 bg-white p-2 rounded-lg shadow flex-1 text-nowrap">
              <input
                type="checkbox"
                className="form-checkbox"
                checked={saltwaterFilter}
                onChange={(event) => setSaltwaterFilter(event.target.checked)}
                data-posthog="filter-water-saltwater"
              />
              Saltwater
            </label>
          </div>
        </div>
      )}
        </ResponsiveFilterSection>
      )}

      {!hideCareRequirements && setMinTankSizeMax && (
        <ResponsiveFilterSection
          title="Care requirements"
          active={Boolean(
            hasCareGuide !== "all" ||
              Number.isFinite(minTankSizeMax) ||
              Number.isFinite(maxSizeMax) ||
              temperatureRange ||
              phRange ||
              lifespanRange ||
              careLevels?.length ||
              reefSafeValues?.length ||
              plantSafeValues?.length ||
              temperamentValues?.length ||
              dietTraits?.length
          )}
        >
        <div className="mb-3 bg-gray-50 p-3 rounded-lg shadow">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Care Requirements
          </label>
          {setHasCareGuide && (
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Care Guide
              </label>
              <div className="flex flex-col gap-1">
                {[
                  { value: "all", label: "All Species" },
                  { value: "yes", label: "Has Care Guide" },
                  { value: "no", label: "No Care Guide" },
                ].map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 bg-white p-1.5 rounded-lg shadow text-sm"
                  >
                    <input
                      type="radio"
                      name="hasCareGuide"
                      checked={hasCareGuide === option.value}
                      onChange={() => {
                        setHasCareGuide(option.value);
                        trackFilterUsage("has_care_guide", option.value);
                      }}
                      data-posthog="filter-has-care-guide"
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
          )}
          {hasCareGuide !== "no" && (
            <CareRequirementsSection
              minTankSizeMax={minTankSizeMax}
              setMinTankSizeMax={setMinTankSizeMax}
              maxSizeMax={maxSizeMax}
              setMaxSizeMax={setMaxSizeMax}
              temperatureRange={temperatureRange}
              setTemperatureRange={setTemperatureRange}
              phRange={phRange}
              setPhRange={setPhRange}
              careLevels={careLevels}
              setCareLevels={setCareLevels}
              reefSafeValues={reefSafeValues}
              setReefSafeValues={setReefSafeValues}
              plantSafeValues={plantSafeValues}
              setPlantSafeValues={setPlantSafeValues}
              temperamentValues={temperamentValues}
              setTemperamentValues={setTemperamentValues}
              dietTraits={dietTraits}
              setDietTraits={setDietTraits}
              lifespanRange={lifespanRange}
              setLifespanRange={setLifespanRange}
              availableCareLevels={availableCareLevels}
              availableReefSafe={availableReefSafe}
              availablePlantSafe={availablePlantSafe}
              availableTemperaments={availableTemperaments}
              availableDietTraits={availableDietTraits}
              temperatureBounds={temperatureBounds}
              maxTankSizeBound={maxTankSizeBound}
              maxSizeBound={maxSizeBound}
              phBounds={phBounds}
              lifespanBounds={lifespanBounds}
              trackFilterUsage={trackFilterUsage}
            />
          )}
        </div>
        </ResponsiveFilterSection>
      )}

      {(!hideStoreFilter || !hidePersonalLists) && (
        <ResponsiveFilterSection
          title="Stores & saved lists"
          active={Boolean(favoritesOnly || notificationsOnly || reefWizardOnly)}
        >
      {!hideStoreFilter && (
        <div className="grow mb-3 bg-gray-50 p-3 rounded-lg shadow">
          <SearchableCheckboxList
            label="Stores"
            options={availableStores.filter((storeId) => storesData[storeId])}
            selectedOptions={allowedStores || []}
            onSelectionChange={setAllowedStores}
            displayFormatter={(storeId) => storesData[storeId] || storeId}
            posthogId="filter-stores-select"
            posthogAttribute="data-posthog-store-id"
            maxHeight="30vh"
          />
        </div>
      )}

      {!hidePersonalLists && (
        <PersonalListsFilter
          favoritesOnly={favoritesOnly}
          setFavoritesOnly={setFavoritesOnly}
          notificationsOnly={notificationsOnly}
          setNotificationsOnly={setNotificationsOnly}
          reefWizardOnly={reefWizardOnly}
          setReefWizardOnly={setReefWizardOnly}
          reefWizardTankFilter={reefWizardTankFilter}
          setReefWizardTankFilter={setReefWizardTankFilter}
          reefWizardTanks={reefWizardTanks}
        />
      )}
        </ResponsiveFilterSection>
      )}

      {(!hideTaxonomy || !hideSpecies) && (
        <ResponsiveFilterSection
          title="Taxonomy"
          active={Boolean(
            classFilter || orderFilter || familyFilter || genusFilter || scientificFilter
          )}
        >
      {!hideTaxonomy && (
        <div className="flex flex-row flex-wrap justify-between items-center gap-3 mb-3 bg-gray-50 p-3 rounded-lg shadow">
          <TaxonFilter
            label="Class"
            pluralLabel="Classes"
            options={filteredClasses}
            unfilteredOptions={unfilteredClasses}
            value={classFilter}
            onChange={setClassFilter}
          />
          <TaxonFilter
            label="Order"
            pluralLabel="Orders"
            options={filteredOrders}
            unfilteredOptions={unfilteredOrders}
            value={orderFilter}
            onChange={setOrderFilter}
          />
          <TaxonFilter
            label="Family"
            pluralLabel="Families"
            options={filteredFamilies}
            unfilteredOptions={unfilteredFamilies}
            value={familyFilter}
            onChange={setFamilyFilter}
          />
          <TaxonFilter
            label="Genus"
            pluralLabel="Genera"
            options={filteredGenera}
            unfilteredOptions={unfilteredGenera}
            value={genusFilter}
            onChange={setGenusFilter}
          />
        </div>
      )}

      {!hideSpecies && (
        <div className="mb-3 w-full bg-gray-50 p-3 rounded-lg shadow">
          <SearchableSelect
            label="Species"
            value={scientificFilter}
            onChange={setScientificFilter}
            options={filteredScientificNames}
            unfilteredOptions={unfilteredScientificNames}
            placeholder="Select species..."
            allOptionLabel="All Species"
            displayFormatter={(name) => `${speciesMap[name] || ""} - ${name}`}
            posthogId="filter-species"
            posthogAttribute="data-posthog-species"
          />
        </div>
      )}
        </ResponsiveFilterSection>
      )}
    </div>
  );
};

const CareRequirementsSection = ({
  minTankSizeMax,
  setMinTankSizeMax,
  maxSizeMax,
  setMaxSizeMax,
  temperatureRange,
  setTemperatureRange,
  phRange,
  setPhRange,
  careLevels,
  setCareLevels,
  reefSafeValues,
  setReefSafeValues,
  plantSafeValues,
  setPlantSafeValues,
  temperamentValues,
  setTemperamentValues,
  dietTraits,
  setDietTraits,
  lifespanRange,
  setLifespanRange,
  availableCareLevels,
  availableReefSafe,
  availablePlantSafe,
  availableTemperaments,
  availableDietTraits,
  temperatureBounds,
  maxTankSizeBound,
  maxSizeBound,
  phBounds,
  lifespanBounds,
  trackFilterUsage,
}) => {
  const formatNumber = (value, decimals = 0) => {
    if (value === null || value === undefined) return "";
    if (decimals === 0 || Number.isInteger(value)) {
      return String(value);
    }
    return Number(value)
      .toFixed(decimals)
      .replace(/\.0+$/, "")
      .replace(/(\.\d*[1-9])0+$/, "$1");
  };

  const handleCareLevelChange = (level) => {
    const updated = careLevels.includes(level)
      ? careLevels.filter((value) => value !== level)
      : [...careLevels, level];
    setCareLevels(updated);
    trackFilterUsage("care_level", updated.join(","));
  };

  const handleReefSafeChange = (value) => {
    const updated = reefSafeValues.includes(value)
      ? reefSafeValues.filter((entry) => entry !== value)
      : [...reefSafeValues, value];
    setReefSafeValues(updated);
    trackFilterUsage("reef_safe", updated.join(","));
  };

  const handlePlantSafeChange = (value) => {
    const updated = plantSafeValues.includes(value)
      ? plantSafeValues.filter((entry) => entry !== value)
      : [...plantSafeValues, value];
    setPlantSafeValues(updated);
    trackFilterUsage("plant_safe", updated.join(","));
  };

  const handleTemperamentChange = (value) => {
    const updated = temperamentValues.includes(value)
      ? temperamentValues.filter((entry) => entry !== value)
      : [...temperamentValues, value];
    setTemperamentValues(updated);
    trackFilterUsage("temperament", updated.join(","));
  };

  const handleDietTraitChange = (value) => {
    const updated = dietTraits.includes(value)
      ? dietTraits.filter((entry) => entry !== value)
      : [...dietTraits, value];
    setDietTraits(updated);
    trackFilterUsage("diet_traits", updated.join(","));
  };

  return (
    <div className="flex flex-col gap-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Minimum Tank Size
        </label>
        <div className="px-1">
          <Range
            step={10}
            min={0}
            max={maxTankSizeBound}
            values={[minTankSizeMax ?? maxTankSizeBound]}
            onChange={(values) => {
              setMinTankSizeMax(values[0] >= maxTankSizeBound ? null : values[0]);
              trackFilterUsage("min_tank_size", values[0]);
            }}
            renderTrack={({ props, children }) => (
              <div
                {...props}
                style={{
                  ...props.style,
                  height: "6px",
                  background: `linear-gradient(to right, #007bff ${
                    (((minTankSizeMax ?? maxTankSizeBound) - 0) /
                      (maxTankSizeBound - 0)) *
                    100
                  }%, #ccc ${
                    (((minTankSizeMax ?? maxTankSizeBound) - 0) /
                      (maxTankSizeBound - 0)) *
                    100
                  }%)`,
                  borderRadius: "3px",
                }}
              >
                {children}
              </div>
            )}
            renderThumb={({ props }) => (
              <div
                {...props}
                style={{
                  ...props.style,
                  height: "16px",
                  width: "16px",
                  backgroundColor: "#007bff",
                  borderRadius: "50%",
                }}
              />
            )}
          />
          <div className="flex justify-between text-xs mt-1 text-gray-500">
            <span>0 gal</span>
            <span>{minTankSizeMax !== null ? `<= ${minTankSizeMax} gal` : "Any"}</span>
          </div>
        </div>
      </div>

      {setMaxSizeMax && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Max Adult Size
          </label>
          <div className="px-1">
            <Range
              step={0.5}
              min={0}
              max={maxSizeBound}
              values={[maxSizeMax ?? maxSizeBound]}
              onChange={(values) => {
                const nextValue = Number(values[0].toFixed(1));
                setMaxSizeMax(nextValue >= maxSizeBound ? null : nextValue);
                trackFilterUsage("max_size", nextValue);
              }}
              renderTrack={({ props, children }) => (
                <div
                  {...props}
                  style={{
                    ...props.style,
                    height: "6px",
                    background: `linear-gradient(to right, #007bff ${
                      (((maxSizeMax ?? maxSizeBound) - 0) / (maxSizeBound - 0)) *
                      100
                    }%, #ccc ${
                      (((maxSizeMax ?? maxSizeBound) - 0) / (maxSizeBound - 0)) *
                      100
                    }%)`,
                    borderRadius: "3px",
                  }}
                >
                  {children}
                </div>
              )}
              renderThumb={({ props }) => (
                <div
                  {...props}
                  style={{
                    ...props.style,
                    height: "16px",
                    width: "16px",
                    backgroundColor: "#007bff",
                    borderRadius: "50%",
                  }}
                />
              )}
            />
            <div className="flex justify-between text-xs mt-1 text-gray-500">
              <span>0"</span>
              <span>{maxSizeMax !== null ? `<= ${formatNumber(maxSizeMax, 1)}"` : "Any"}</span>
            </div>
          </div>
        </div>
      )}

      {setTemperatureRange && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Temperature Range (°F)
          </label>
          <div className="px-1">
            <Range
              step={1}
              min={temperatureBounds[0]}
              max={temperatureBounds[1]}
              values={temperatureRange || temperatureBounds}
              onChange={(values) => {
                const isDefault =
                  values[0] === temperatureBounds[0] &&
                  values[1] === temperatureBounds[1];
                setTemperatureRange(isDefault ? null : values);
                trackFilterUsage("temperature_range", `${values[0]}-${values[1]}`);
              }}
              renderTrack={({ props, children }) => {
                const values = temperatureRange || temperatureBounds;
                return (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      height: "6px",
                      background: `linear-gradient(to right, #ccc ${
                        ((values[0] - temperatureBounds[0]) /
                          (temperatureBounds[1] - temperatureBounds[0])) *
                        100
                      }%, #007bff ${
                        ((values[0] - temperatureBounds[0]) /
                          (temperatureBounds[1] - temperatureBounds[0])) *
                        100
                      }%, #007bff ${
                        ((values[1] - temperatureBounds[0]) /
                          (temperatureBounds[1] - temperatureBounds[0])) *
                        100
                      }%, #ccc ${
                        ((values[1] - temperatureBounds[0]) /
                          (temperatureBounds[1] - temperatureBounds[0])) *
                        100
                      }%)`,
                      borderRadius: "3px",
                    }}
                  >
                    {children}
                  </div>
                );
              }}
              renderThumb={({ props }) => (
                <div
                  {...props}
                  style={{
                    ...props.style,
                    height: "16px",
                    width: "16px",
                    backgroundColor: "#007bff",
                    borderRadius: "50%",
                  }}
                />
              )}
            />
            <div className="flex justify-between text-xs mt-1 text-gray-500">
              <span>{(temperatureRange || temperatureBounds)[0]}°F</span>
              <span>{(temperatureRange || temperatureBounds)[1]}°F</span>
            </div>
          </div>
        </div>
      )}

      {setLifespanRange && lifespanBounds && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Lifespan
          </label>
          <div className="px-1">
            <Range
              step={0.5}
              min={lifespanBounds[0]}
              max={lifespanBounds[1]}
              values={lifespanRange || lifespanBounds}
              onChange={(values) => {
                const nextValues = [
                  Number(values[0].toFixed(1)),
                  Number(values[1].toFixed(1)),
                ];
                const isDefault =
                  nextValues[0] === lifespanBounds[0] &&
                  nextValues[1] === lifespanBounds[1];
                setLifespanRange(isDefault ? null : nextValues);
                trackFilterUsage("lifespan_range", `${nextValues[0]}-${nextValues[1]}`);
              }}
              renderTrack={({ props, children }) => {
                const values = lifespanRange || lifespanBounds;
                return (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      height: "6px",
                      background: `linear-gradient(to right, #ccc ${
                        ((values[0] - lifespanBounds[0]) /
                          (lifespanBounds[1] - lifespanBounds[0])) *
                        100
                      }%, #007bff ${
                        ((values[0] - lifespanBounds[0]) /
                          (lifespanBounds[1] - lifespanBounds[0])) *
                        100
                      }%, #007bff ${
                        ((values[1] - lifespanBounds[0]) /
                          (lifespanBounds[1] - lifespanBounds[0])) *
                        100
                      }%, #ccc ${
                        ((values[1] - lifespanBounds[0]) /
                          (lifespanBounds[1] - lifespanBounds[0])) *
                        100
                      }%)`,
                      borderRadius: "3px",
                    }}
                  >
                    {children}
                  </div>
                );
              }}
              renderThumb={({ props }) => (
                <div
                  {...props}
                  style={{
                    ...props.style,
                    height: "16px",
                    width: "16px",
                    backgroundColor: "#007bff",
                    borderRadius: "50%",
                  }}
                />
              )}
            />
            <div className="flex justify-between text-xs mt-1 text-gray-500">
              <span>{formatNumber((lifespanRange || lifespanBounds)[0], 1)} years</span>
              <span>{formatNumber((lifespanRange || lifespanBounds)[1], 1)} years</span>
            </div>
          </div>
        </div>
      )}

      {setPhRange && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            pH Range
          </label>
          <div className="px-1">
            <Range
              step={0.1}
              min={phBounds[0]}
              max={phBounds[1]}
              values={phRange || phBounds}
              onChange={(values) => {
                const nextValues = [
                  Number(values[0].toFixed(1)),
                  Number(values[1].toFixed(1)),
                ];
                const isDefault =
                  nextValues[0] === phBounds[0] &&
                  nextValues[1] === phBounds[1];
                setPhRange(isDefault ? null : nextValues);
                trackFilterUsage("ph_range", `${nextValues[0]}-${nextValues[1]}`);
              }}
              renderTrack={({ props, children }) => {
                const values = phRange || phBounds;
                return (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      height: "6px",
                      background: `linear-gradient(to right, #ccc ${
                        ((values[0] - phBounds[0]) / (phBounds[1] - phBounds[0])) *
                        100
                      }%, #007bff ${
                        ((values[0] - phBounds[0]) / (phBounds[1] - phBounds[0])) *
                        100
                      }%, #007bff ${
                        ((values[1] - phBounds[0]) / (phBounds[1] - phBounds[0])) *
                        100
                      }%, #ccc ${
                        ((values[1] - phBounds[0]) / (phBounds[1] - phBounds[0])) *
                        100
                      }%)`,
                      borderRadius: "3px",
                    }}
                  >
                    {children}
                  </div>
                );
              }}
              renderThumb={({ props }) => (
                <div
                  {...props}
                  style={{
                    ...props.style,
                    height: "16px",
                    width: "16px",
                    backgroundColor: "#007bff",
                    borderRadius: "50%",
                  }}
                />
              )}
            />
            <div className="flex justify-between text-xs mt-1 text-gray-500">
              <span>{formatNumber((phRange || phBounds)[0], 1)}</span>
              <span>{formatNumber((phRange || phBounds)[1], 1)}</span>
            </div>
          </div>
        </div>
      )}

      {availableCareLevels.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Care Level
          </label>
          <div className="flex flex-col gap-1">
            {availableCareLevels.map((level) => (
              <label
                key={level}
                className="flex items-center gap-2 bg-white p-1.5 rounded-lg shadow text-sm"
              >
                <input
                  type="checkbox"
                  className="form-checkbox"
                  checked={careLevels.includes(level)}
                  onChange={() => handleCareLevelChange(level)}
                  data-posthog="filter-care-level"
                />
                <span className="capitalize">{level}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {availableReefSafe.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Reef Safe
          </label>
          <div className="flex flex-col gap-1">
            {availableReefSafe.map((value) => (
              <label
                key={value}
                className="flex items-center gap-2 bg-white p-1.5 rounded-lg shadow text-sm"
              >
                <input
                  type="checkbox"
                  className="form-checkbox"
                  checked={reefSafeValues.includes(value)}
                  onChange={() => handleReefSafeChange(value)}
                  data-posthog="filter-reef-safe"
                />
                <span className="capitalize">{value}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {availablePlantSafe.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Plant Safe
          </label>
          <div className="flex flex-col gap-1">
            {availablePlantSafe.map((value) => (
              <label
                key={value}
                className="flex items-center gap-2 bg-white p-1.5 rounded-lg shadow text-sm"
              >
                <input
                  type="checkbox"
                  className="form-checkbox"
                  checked={plantSafeValues.includes(value)}
                  onChange={() => handlePlantSafeChange(value)}
                  data-posthog="filter-plant-safe"
                />
                <span className="capitalize">{value}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {availableTemperaments.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Temperament
          </label>
          <div className="flex flex-col gap-1">
            {availableTemperaments.map((value) => (
              <label
                key={value}
                className="flex items-center gap-2 bg-white p-1.5 rounded-lg shadow text-sm"
              >
                <input
                  type="checkbox"
                  className="form-checkbox"
                  checked={temperamentValues.includes(value)}
                  onChange={() => handleTemperamentChange(value)}
                  data-posthog="filter-temperament"
                />
                <span className="capitalize">{value}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {setDietTraits && availableDietTraits.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Diet
          </label>
          <div className="flex flex-col gap-1">
            {availableDietTraits.map((value) => (
              <label
                key={value}
                className="flex items-center gap-2 bg-white p-1.5 rounded-lg shadow text-sm"
              >
                <input
                  type="checkbox"
                  className="form-checkbox"
                  checked={dietTraits.includes(value)}
                  onChange={() => handleDietTraitChange(value)}
                  data-posthog="filter-diet"
                />
                <span>{CARE_GUIDE_DIET_TRAIT_LABELS[value] || value}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Filters;
